import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import axios from 'axios';
import { useContent } from '../contexts/ContentContext';

export default function ContentEditModal({ isOpen, onClose }) {
  const { content, updateContent, t } = useContent();
  const [editedContent, setEditedContent] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState({});

  useEffect(() => {
    if (isOpen) {
      const initialContent = { ...content };
      setEditedContent(initialContent);
      categorizeStrings(initialContent);
    }
  }, [isOpen, content]);

  const categorizeStrings = (strings) => {
    const cats = {};
    Object.keys(strings).forEach(key => {
      const prefix = key.split('.')[0];
      if (!cats[prefix]) {
        cats[prefix] = [];
      }
      cats[prefix].push(key);
    });
    setCategories(cats);
    console.log('Categories set:', Object.keys(cats), 'Total keys:', Object.values(cats).flat().length);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // axios already has Authorization header set in defaults, so no need to manually add it
      console.log('📤 Sending request to /admin/content-strings');
      console.log('📦 Payload keys:', Object.keys(editedContent).length);
      
      // Use relative URL since axios is configured with baseURL
      const response = await axios.put(
        '/admin/content-strings',
        { strings: editedContent }
      );
      
      console.log('✅ Response:', response.data);
      
      // Update context immediately
      updateContent(editedContent);
      
      // Show success toast
      console.log('Content updated successfully');
      alert('Content updated successfully!');
      onClose();
    } catch (error) {
      console.error('❌ Failed to save content:', error);
      console.error('Error response:', error.response);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to save content strings';
      alert(`Error: ${errorMsg}`);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (key, value) => {
    setEditedContent(prev => {
      const updated = {
        ...prev,
        [key]: value
      };
      console.log('Content updated:', key, '=', value);
      return updated;
    });
  };

  // Calculate filtered categories - search against ORIGINAL content only, not edited content
  const filteredCategories = useMemo(() => {
    // If no search query, return all categories
    if (!searchQuery || searchQuery.trim() === '') {
      console.log('No search query, returning all', Object.keys(categories).length, 'categories');
      return categories;
    }
    
    // Split search query into multiple keywords/phrases
    // Supports: "upload button", "upload, button", "upload|button", etc.
    const searchTerms = searchQuery
      .toLowerCase()
      .split(/[\s,|]+/)  // Split by space, comma, or pipe
      .filter(term => term.length > 0);  // Remove empty strings
    
    console.log('Search terms:', searchTerms);
    
    const filtered = {};
    
    Object.keys(categories).forEach(category => {
      // Match against key name AND ORIGINAL content value (not edited value)
      // This prevents items from disappearing while you're editing them
      const matchingKeys = categories[category].filter(key => {
        const keyLower = key.toLowerCase();
        const originalValueLower = (content[key] || '').toLowerCase();
        
        // Check if ALL search terms match (AND logic) either in key OR original value
        return searchTerms.every(term => 
          keyLower.includes(term) || originalValueLower.includes(term)
        );
      });
      
      if (matchingKeys.length > 0) {
        filtered[category] = matchingKeys;
      }
    });
    
    console.log('Search active, filtered to', Object.keys(filtered).length, 'categories');
    return filtered;
  }, [searchQuery, categories, content]);

  const getCategoryName = (prefix) => {
    const names = {
      'toast': 'Toast Messages',
      'dialog': 'Dialog Messages',
      'btn': 'Button Labels',
      'form': 'Form Labels',
      'page': 'Page Titles',
      'nav': 'Navigation',
      'upload': 'Upload Page',
      'cart': 'Cart Page',
      'checkout': 'Checkout Page',
      'orders': 'Orders Page',
      'login': 'Login Page',
      'register': 'Register Page',
      'settings': 'Settings Page',
      'admin': 'Admin Section',
      'error': 'Error Messages',
      'success': 'Success Messages',
      'tooltip': 'Tooltips',
      'editor': 'Image Editor Labels',
      'placeholder': 'Form Placeholders',
      'content': 'Content Management',
      'hero': 'Hero/Landing Page'
    };
    return names[prefix] || prefix.charAt(0).toUpperCase() + prefix.slice(1);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>{t('dialog.content_editor', 'Content Editor')}</DialogTitle>
          <DialogDescription>
            {t('dialog.content_editor_desc', 'Edit all text content across your application. Changes apply immediately after saving.')}
          </DialogDescription>
        </DialogHeader>

        {/* Search Bar */}
        <div className="mb-4">
          <input
            type="text"
            placeholder="Search by keywords or phrases (e.g., 'upload button' or 'cart, checkout')..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              // Prevent modal from closing when typing
              e.stopPropagation();
            }}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
          />
          {searchQuery && (
            <p className="text-sm text-gray-500 mt-1">
              Searching for: <span className="font-medium">{searchQuery.split(/[\s,|]+/).filter(t => t).join(', ')}</span>
            </p>
          )}
        </div>

        {/* Content List */}
        <div className="flex-1 overflow-y-auto space-y-6 pr-2">
          {Object.entries(filteredCategories).map(([category, keys]) => (
            <div key={category} className="border-b pb-4">
              <h3 className="text-lg font-semibold mb-3 text-custom-orange">
                {getCategoryName(category)}
              </h3>
              <div className="space-y-3">
                {keys.map(key => (
                  <div key={key} className="grid grid-cols-3 gap-4 items-start">
                    <div className="col-span-1">
                      <label className="text-sm font-medium text-gray-700 break-all">
                        {key}
                      </label>
                    </div>
                    <div className="col-span-2">
                      <textarea
                        value={editedContent[key] || ''}
                        onChange={(e) => handleChange(key, e.target.value)}
                        onKeyDown={(e) => {
                          // Prevent modal from closing when typing
                          e.stopPropagation();
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500 min-h-[60px]"
                        rows={2}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer Buttons */}
        <DialogFooter className="mt-4">
          <Button
            variant="outline"
            onClick={async () => {
              try {
                console.log('🔄 Merging default strings...');
                const response = await axios.post('/admin/content-strings/merge-defaults');
                console.log('✅ Merge response:', response.data);
                
                const addedCount = response.data.added_count || 0;
                alert(`✅ Merge complete! Added ${addedCount} new default strings.`);
                
                // Refresh content from the response (it already contains merged strings)
                if (response.data.strings) {
                  setEditedContent(response.data.strings);
                  categorizeStrings(response.data.strings);
                  console.log('✅ Content refreshed');
                }
              } catch (error) {
                console.error('❌ Failed to merge defaults:', error);
                alert(`Failed to merge defaults: ${error.response?.data?.detail || error.message}`);
              }
            }}
            disabled={saving}
            className="mr-auto"
            title="Adds new default strings without overwriting your existing edits"
          >
            🔄 Merge Defaults
          </Button>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-custom-orange hover:bg-custom-orange"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
